import React from 'react';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { SignalTone, tokens } from '../../theme/tokens';

/** What a KPI is signalling: colours the value and the delta. */
export type KpiTone = 'none' | SignalTone | 'muted';

export interface KpiCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** A change figure shown beside the value, coloured by `tone`. */
  delta?: React.ReactNode;
  /** A muted line under the value. */
  note?: React.ReactNode;
  tone?: KpiTone;
  loading?: boolean;
  /**
   * Whether `tone` colours the value as well as the delta. The dashboards'
   * amber flag keeps the value in ink and colours only the delta.
   */
  colorValue?: boolean;
  /**
   * `card` is a bordered tile. `cell` is one cell of a strip that draws its
   * own hairlines, for the dashboards' KPI band.
   */
  variant?: 'card' | 'cell';
  onClick?: () => void;
}

const valueInk = (tone: KpiTone): string => {
  if (tone === 'none') return tokens.ink.primary;
  if (tone === 'muted') return tokens.ink.muted;
  return tokens.signal[tone].ink;
};

const deltaInk = (tone: KpiTone): string => {
  if (tone === 'none') return tokens.signal.positive.ink;
  if (tone === 'muted') return tokens.ink.muted;
  return tokens.signal[tone].ink;
};

/**
 * The one KPI tile. Three implementations used to exist with three value
 * sizes (34px, 27px, 23px) and three tone mechanisms.
 */
export const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  delta,
  note,
  tone = 'none',
  loading = false,
  colorValue = true,
  variant = 'card',
  onClick,
}) => {
  const body = (
    <>
      <Typography variant="overline" component="div" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" animation="wave" width={72} height={34} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colorValue ? valueInk(tone) : valueInk(tone === 'muted' ? 'muted' : 'none'),
            }}
          >
            {value}
          </Typography>
          {delta && (
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: deltaInk(tone) }}>
              {delta}
            </Typography>
          )}
        </Box>
      )}
      {note && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {note}
        </Typography>
      )}
    </>
  );

  if (variant === 'cell') {
    return (
      <Box
        onClick={onClick}
        sx={{
          p: '14px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
          minWidth: 0,
          borderRight: `1px solid ${tokens.line.hairline}`,
          borderBottom: `1px solid ${tokens.line.hairline}`,
          cursor: onClick ? 'pointer' : undefined,
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Card
      onClick={onClick}
      sx={{ p: '15px 17px', display: 'flex', flexDirection: 'column', gap: '5px', cursor: onClick ? 'pointer' : undefined }}
    >
      {body}
    </Card>
  );
};

/** Responsive grid for a row of `KpiCard`s. `cell` variant cards get no gap. */
export const KpiGrid: React.FC<{
  children: React.ReactNode;
  variant?: 'card' | 'cell';
  minWidth?: number;
  /**
   * Fixed column count from `md` up (two from `sm`). Use it when the tile count
   * divides evenly, so the last row is never left with empty cells.
   */
  columns?: number;
}> = ({ children, variant = 'card', minWidth = 180, columns }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: columns
        ? { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))', md: `repeat(${columns}, minmax(0,1fr))` }
        : { xs: 'minmax(0,1fr)', sm: `repeat(auto-fit, minmax(${minWidth}px, 1fr))` },
      gap: variant === 'cell' ? 0 : '14px',
      ...(variant === 'cell'
        ? { bgcolor: tokens.surface.card, borderBottom: `1px solid ${tokens.line.default}` }
        : null),
    }}
  >
    {children}
  </Box>
);
